/**
 * Process Steps Chart
 * 
 * A simple D3-based vertical bar chart showing process steps.
 * X-axis: Steps in sequence (left to right)
 * Y-axis: Count
 */

import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import styles from './ProcessStepsChart.module.css';

export interface ProcessStep {
  id: string;
  name: string;
  count: number;
  color?: string;
}

interface ProcessStepsChartProps {
  /** Steps in sequence order */
  steps: ProcessStep[];
  /** Chart height in pixels */
  height?: number;
  /** Default bar color */
  barColor?: string;
  /** Show value labels on bars */
  showLabels?: boolean;
  /** Show conversion rates between steps */
  showConversionRates?: boolean;
  /** Compact mode */
  compact?: boolean;
}

const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

export const ProcessStepsChart: React.FC<ProcessStepsChartProps> = ({
  steps,
  height = 180,
  barColor = '#3b82f6',
  showLabels = true,
  showConversionRates = true,
  compact = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const maxValue = useMemo(() => {
    const max = Math.max(...steps.map(s => s.count), 1);
    return max * 1.15;
  }, [steps]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || steps.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);
    
    // ISSUE-058/066 fix: Use requestAnimationFrame to batch DOM reads and avoid forced reflows
    const animationFrameId = requestAnimationFrame(() => {
      if (!svgRef.current || !containerRef.current) return;
      
      svg.selectAll('*').remove();

      const margin = { 
        top: showLabels ? 25 : 10, 
        right: 10, 
        bottom: compact ? 40 : 50, 
        left: compact ? 35 : 45 
      };
      const width = container.clientWidth;
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X scale
    const x = d3.scaleBand()
      .domain(steps.map(s => s.id))
      .range([0, innerWidth])
      .padding(0.3);

    // Y scale
    const y = d3.scaleLinear()
      .domain([0, maxValue])
      .nice()
      .range([innerHeight, 0]);

    // X axis
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).tickFormat(d => {
        const step = steps.find(s => s.id === d);
        return step?.name ?? d;
      }));

    xAxis.selectAll('text')
      .attr('transform', compact ? 'rotate(-45)' : 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em')
      .style('font-size', compact ? '9px' : '10px')
      .style('fill', '#6b7280');

    xAxis.select('.domain').style('stroke', '#e5e7eb');
    xAxis.selectAll('.tick line').style('stroke', '#e5e7eb');

    // Y axis
    const yAxisGroup = g.append('g')
      .call(d3.axisLeft(y).ticks(4).tickFormat(d => formatNumber(d as number)));
    
    yAxisGroup.selectAll('text')
      .style('font-size', compact ? '9px' : '10px')
      .style('fill', '#6b7280');
    
    yAxisGroup.select('.domain').style('stroke', '#e5e7eb');
    yAxisGroup.selectAll('.tick line').style('stroke', '#e5e7eb');

    // Draw bars
    steps.forEach((step, index) => {
      const color = step.color || barColor;
      const barX = x(step.id)!;
      const barY = y(step.count);
      const barHeight = innerHeight - barY;
      const barWidth = x.bandwidth();

      // Bar with animation
      const bar = g.append('rect')
        .attr('x', barX)
        .attr('y', innerHeight)
        .attr('width', barWidth)
        .attr('height', 0)
        .attr('fill', color)
        .attr('rx', 3);

      bar.transition()
        .duration(400)
        .delay(index * 60)
        .attr('y', barY)
        .attr('height', barHeight);

      // Value label
      if (showLabels) {
        g.append('text')
          .attr('x', barX + barWidth / 2)
          .attr('y', barY - 4)
          .attr('text-anchor', 'middle')
          .attr('fill', color)
          .style('font-size', compact ? '9px' : '10px')
          .style('font-weight', '600')
          .text(formatNumber(step.count))
          .attr('opacity', 0)
          .transition()
          .duration(400)
          .delay(index * 60)
          .attr('opacity', 1);
      }

      // Conversion rate arrow between bars
      if (showConversionRates && index > 0) {
        const prevStep = steps[index - 1];
        const conversionRate = prevStep.count > 0 
          ? Math.round((step.count / prevStep.count) * 100) 
          : 0;
        
        const prevX = x(prevStep.id)! + x.bandwidth();
        const midX = (prevX + barX) / 2;
        
        // Small conversion label below axis
        g.append('text')
          .attr('x', midX)
          .attr('y', innerHeight + (compact ? 12 : 15))
          .attr('text-anchor', 'middle')
          .attr('fill', '#9ca3af')
          .style('font-size', compact ? '8px' : '9px')
          .text(`${conversionRate}%`);
      }
    });
    }); // End of requestAnimationFrame
    
    // ISSUE-058/066 fix: Cleanup animation frame on unmount
    return () => cancelAnimationFrame(animationFrameId);
  }, [steps, height, maxValue, barColor, showLabels, showConversionRates, compact]);

  // Handle resize
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      // Re-render on resize
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  if (steps.length === 0) {
    return <div className={styles.empty}>No data available</div>;
  }

  return (
    <div className={styles.chartContainer} ref={containerRef}>
      <svg ref={svgRef} className={styles.chart} />
    </div>
  );
};

export default ProcessStepsChart;
